import { CardGrid } from '@liveauctioneers/hammer-ui-core/card/cardGrid';
import { BodyPrimary, Caption, H1 } from '@liveauctioneers/hammer-ui-core/text';
import { useMemo } from 'react';
import { Navigate } from 'react-router-dom';

import {
    SearchPageItemCard,
    type SearchPageItemCardProps,
} from '@/pages/Search/SearchPage/components/SearchPageTabs/components/SearchPageCardGrid/components/SearchPageItemCard/SearchPageItemCard';
import { useAppSelector } from '@/redux/hooks';
import {
    getImageSearchResults,
    getImageSearchStatus,
    getImageSearchThumbnailUrl,
} from '@/redux/modules/search/imageSearch/imageSearch.selectors';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

/**
 * Ephemeral results page for Search by Image. Renders entirely from the imageSearch
 * slice (populated by the upload popover) — nothing is stored server-side, so a direct
 * load or refresh has no state to rebuild from and redirects home.
 */
const VisualSearchPage = () => {
    const formatMessage = useAppFormatMessage();
    const status = useAppSelector(getImageSearchStatus);
    const results = useAppSelector(getImageSearchResults);
    const thumbnailUrl = useAppSelector(getImageSearchThumbnailUrl);

    const { cardData, cardIds } = useMemo(() => {
        const byId: Record<number, SearchPageItemCardProps> = {};
        const ids: number[] = [];
        results.forEach((item, index) => {
            // Match the keyword-search grid (SearchPageCardGrid) so /visual-search renders the
            // same card treatment: heart favorite icon + the "See Similar Items" CTA.
            byId[item.itemId] = { ...item, position: index + 1, hasNewSimilarItemsIconCTA: true };
            ids.push(item.itemId);
        });
        return { cardData: byId, cardIds: ids };
    }, [results]);

    // Only a genuine direct load / refresh has no search to show (redux rebuilds to the default
    // 'idle' slice). Redirect that case home, but stay put while a re-search from this page is
    // in flight ('analysing') so the upload popover doesn't flash the home page behind it.
    if (status === 'idle') {
        return (
            <Navigate
                replace
                to="/"
            />
        );
    }

    const hasResults = results.length > 0;

    return (
        <div className="m-auto max-w-[1644px] px-md py-md xsMax:px-sm xsMax:py-sm">
            <div className="mb-md flex items-center gap-sm">
                {thumbnailUrl && (
                    <img
                        alt={String(formatMessage(['visualSearch', 'searchByImage']))}
                        className="h-[64px] w-[64px] rounded-md border border-border-tertiary object-cover"
                        src={thumbnailUrl}
                    />
                )}
                <div className="flex flex-col">
                    <Caption className="text-text-secondary uppercase">
                        {formatMessage(['visualSearch', 'searchByImage'])}
                    </Caption>
                    <H1>
                        {hasResults
                            ? formatMessage(['visualSearch', 'similarItems'])
                            : formatMessage(['visualSearch', 'noMatchFound'])}
                    </H1>
                </div>
            </div>

            {hasResults ? (
                <CardGrid
                    card={SearchPageItemCard}
                    cardData={cardData}
                    cards={cardIds}
                    // The card border + rounding live on the <article> wrapper (same as
                    // SearchPageCardGrid / SimilarPageCardGrid): under hasNewSimilarItemsIconCTA the
                    // image drops its own border for a border-b, so the wrapper must supply the frame.
                    className="min-w-0 gap-y-lg mobile:grid-cols-2 mobile:gap-y-md [&>article]:flex [&>article]:h-full [&>article]:min-h-0 [&>article]:flex-col [&>article]:overflow-hidden [&>article]:rounded-md [&>article]:border [&>article]:border-border-secondary [&>article>*:last-child]:mt-auto"
                    colsByBreakpoint={{ lg: 4, md: 4, sm: 3, xl: 4, xs: 2 }}
                    gapsByBreakpoint={{ lg: 'md', md: 'md', sm: 'sm', xl: 'md', xs: 'sm' }}
                />
            ) : (
                <div
                    className="flex flex-col items-center gap-2xs rounded-md bg-background-subdued px-md py-lg text-center"
                    data-testid="visualSearchNoResults"
                >
                    <BodyPrimary>{formatMessage(['visualSearch', 'noResultsHeading'])}</BodyPrimary>
                    <BodyPrimary className="text-text-secondary">
                        {formatMessage(['visualSearch', 'noResultsBody'])}
                    </BodyPrimary>
                </div>
            )}
        </div>
    );
};

export default VisualSearchPage;
